import { ColDef } from 'ag-grid-community';
import {
  CELL_FILTER,
  CELL_RENDERER,
  COLUMN_SELECTOR_ID,
  ENTITY_TYPES,
  IBaseEntityType,
  IDocumentOperationOptions,
  IEntityConfig,
  IInventoryCount,
  IInventoryCountGroup,
  ISite,
  SORT_ORDERS,
} from '../../types';
import { Injectable } from '@angular/core';
import { BaseService } from '../base';
import { detectMobile } from '@geniuspos/ui-shared/modules/xdm-shared-components/components/utils';
import { Subscription } from 'rxjs';
import {
  flow,
  forEach,
  get,
  isEmpty,
  isNil,
  map,
  range,
  set,
  has,
  isNull,
  trim,
} from 'lodash';
import { TIME_FORMATS } from '@config/global-constants';
import {
  PdfDataStructure,
  TableData,
} from '@app/shared/common/make-pdf/pdf-data-structure.type';
import { INVENTORY_COUNT_GROUP_PDF_HEADERS } from '@app/shared/types/enums/inventory-count-pdf.enum';
import { lazyInject } from '@containers/base/decorators';
import { PermissionService } from '@shared/services/session/permission.service';
import * as moment from 'moment-timezone';
import {
  INVENTORY_COUNT_STATUS_ENUM_OPTIONS,
  INVENTORY_COUNT_STATUS_OPTIONS,
} from '@shared/types/enums/inventory-count.enum';
import { toMoment } from '@shared/utils';
import { I18n } from '@app/i18n/strings-keys';

const entityType: ENTITY_TYPES = ENTITY_TYPES.INVENTORY_COUNT_GROUP;
const config: IEntityConfig = {
  ENTITY_TYPE: entityType,
  isMasterListAvailable: true,
  apiEndpoint: entityType,
  uiRoute: entityType,
  isFormServiceSupported: true,
  entityNameToCheckPermissions: ENTITY_TYPES.INVENTORY_COUNT,
  needConvertTimeZoneFields: ['start_date', 'end_date'],
  timeZoneTimeFormats: TIME_FORMATS.L_LT,
  suppressPermissionEntityTypeUsageForNavigation: true,
  list: {
    userGridOptions: {
      suppressRowClickSelection: true,
    },
    gridOptions: {
      valueField: '_id',
      disableColumnSelector: true,
      multiselect: false,
      disableUserViews: true,
      showViewSelector: false,
    },
    documentQuery: {
      orderby: 'start_date desc',
      notNeedActive: true,
      select: ['entity_id', 'site_ids'],
      check_is_removable: true,
    },
    documentOperationOptions: {
      skipMappers: true,
    },
    columns: [
      {
        path: 'name',
        title: I18n.Common.Name,
        filterType: CELL_FILTER.string,
        filterParams: {
          quickFilter: true,
        },
        sortable: true,
        cellRenderer: CELL_RENDERER.entity_link_renderer,
        defaultView: true,
      },
      {
        path: 'start_date',
        title: I18n.Forms.InventoryCountForm.StartDateStartTime,
        sortable: true,
        sortingOrder: [SORT_ORDERS.DESC, SORT_ORDERS.ASC],
        defaultView: true,
      },
      {
        path: 'end_date',
        title: I18n.Forms.InventoryCountForm.EndDateEndTime,
        sortable: true,
        sortingOrder: [SORT_ORDERS.DESC, SORT_ORDERS.ASC],
        defaultView: true,
      },
      {
        path: 'status',
        title: I18n.Common.Status,
        sortable: true,
        defaultView: true,
        filterType: CELL_FILTER.enum,
        filterParams: {
          values: INVENTORY_COUNT_STATUS_OPTIONS,
        },
        cellRenderer: CELL_RENDERER.prepared_string_renderer,
        cellRendererParams: {
          valueToEnumMap: INVENTORY_COUNT_STATUS_ENUM_OPTIONS,
        },
      },
      {
        path: COLUMN_SELECTOR_ID,
        width: 50,
        title: '',
        defaultView: true,
        cellRenderer: CELL_RENDERER.site_inventory_count_actions_renderer,
        pinned: detectMobile() ? false : 'right',
        lockPinned: true,
      },
    ],
  },
  buttons: {},
};

/**
 * Inventory Count Group service that extends BaseService to use
 * basic CRUD request operations
 */
@Injectable()
export class InventoryCountGroupService extends BaseService {
  public static config: IEntityConfig = config;
  public static ENTITY_TYPE: string = config.ENTITY_TYPE;
  public static isCompanyLevel: boolean = false;

  @lazyInject(PermissionService) public permissionService: PermissionService;

  public subscriptions: Subscription[] = [];
  public quickFilterFields: string[] = ['name'];
  public listProjection: string[] = ['name'];
  public headerNames: ColDef[] = [
    {
      headerName: 'Name',
      field: 'name',
      filter: false,
    },
  ];

  public frontToBackItemMapper(
    doc: IInventoryCountGroup,
  ): IInventoryCountGroup {
    if (doc.name) set(doc, 'name', trim(doc.name));
    const getSiteIds = (
      document: IInventoryCountGroup,
    ): IInventoryCountGroup => {
      if (!has(document, 'virtual.site_inventory_counts')) return document;

      const siteInventoryCounts = get(
        document,
        'virtual.site_inventory_counts',
        [],
      );
      const siteIds = map(siteInventoryCounts, 'id');
      set(document, 'site_ids', siteIds);

      return document;
    };

    const newDoc = flow(getSiteIds)(doc);

    const startDate = get(doc, 'start_date', null);
    if (!isNil(startDate)) {
      const timezone = get(this.companyService, 'currentCompany.timezone');
      const startDateISO = moment
        .tz(startDate, this.timeZoneTimeFormats, timezone)
        .toISOString();
      set(newDoc, 'start_date', startDateISO);
    }

    return super.frontToBackItemMapper(newDoc);
  }

  public async fetchGetDocumentById(
    documentId: string,
    options: IDocumentOperationOptions = {},
  ): Promise<IInventoryCountGroup> {
    return this.apiActions.fetchGetDocumentById(entityType, documentId, {
      ...options,
      query: {
        get_updated_items: true,
        ...options.query,
      },
    });
  }

  /**
   * @deprecated it not used now, we should use pdf service v2 in child class
   * need remove this method
   */
  public transformDocumentToPdfStructure(
    currentDocument: IBaseEntityType,
  ): PdfDataStructure {
    const createDate = this.companyService.convertUTCDateWithTimeZone(
      toMoment().toISOString(),
      true,
      TIME_FORMATS.L_LT,
    );
    const pdfStructure: PdfDataStructure = {
      createDate,
      filename: 'Inventory Count Sheet',
      equalColumnWidth: true,
      pageTitle: 'Inventory Count Sheet',
      id: get(currentDocument, 'id', ''),
      tableData: [
        {
          headers: [...map(INVENTORY_COUNT_GROUP_PDF_HEADERS, 'headerText')],
          bodyData: [],
        },
      ],
    };

    if (!isEmpty(currentDocument?.items)) {
      const tableData = this.generateTableData(currentDocument);
      const pdfTableData = this.getTableBodyData(
        null,
        tableData,
        null,
        null,
        '',
      );
      pdfStructure.tableData[0].bodyData.push(pdfTableData);
    }

    return pdfStructure;
  }

  // eslint-disable-next-line max-params
  public getTableBodyData(
    site: ISite,
    tableData: TableData[],
    ids: string[],
    index: number,
    inventoryName: string,
  ): Object {
    const tableTitle: string =
      !isNull(site) && !isEmpty(inventoryName)
        ? `Location: ${site?.store_number} - ${site?.name}                               ${inventoryName}                     Initials:`
        : '';
    const retStruct = {
      tableTitle,
      data: tableData,
      postBodyData: [
        { text: 'Counted by:' },
        { text: 'Scribed by:' },
        ...(!isNull(ids) && !isNull(index)
          ? [
              {
                text: 'Uploaded by:',
                pageBreak: ids.length - 1 === index ? '' : 'after',
              },
            ]
          : [{ text: 'Uploaded by:' }]),
      ],
    };

    return retStruct;
  }

  public createInventoryCounts(
    documents: IInventoryCount[],
  ): Promise<any[]> | null {
    return isEmpty(documents)
      ? null
      : Promise.all(
          map(documents, (count) =>
            this.apiActions.createEntityAsPromised(
              ENTITY_TYPES.INVENTORY_COUNT,
              count,
              {
                suppressSuccessMessage: true,
              },
            ),
          ),
        );
  }

  private generateTableData(currentDocument: IBaseEntityType): any[] {
    const tableData = [];
    forEach(currentDocument.items, (item: any) => {
      const tbodyRow = [item.name, item.site_name];
      const counts = range(1, 4);
      forEach(counts, (val: number, i: number) => {
        if (!isEmpty(item.measure[i])) {
          tbodyRow.push(item.measure[i].name ?? '');
          tbodyRow.push(String(item.measure[i].count) ?? '0');
        } else {
          tbodyRow.push('');
          tbodyRow.push('');
        }
      });
      tableData.push(tbodyRow);
    });

    return tableData;
  }
}
